<script setup>
// One feature, opened from its card on /features: the claim, the real
// component where there is one, the detail lines, and a way in.
import TlDialog from '~/components/TlDialog.vue';
import FeatureDemo from '~/components/features/FeatureDemo.vue';
import MarketingCtaBtn from '~/components/marketing/MarketingCtaBtn.vue';
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import {FEATURE_TAGS} from '~/utils/features';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  feature: {type: Object, default: null},
});
const emit = defineEmits(['update:modelValue']);
const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});
const {accountCtaLabel} = useMarketingCta();
</script>

<template>
  <tl-dialog v-model="open" :title="feature?.title" :max-width="feature?.demo ? 640 : 480">
    <template v-if="feature">
      <span v-if="feature.tag" class="fdl-tag" :class="`fdl-tag--${feature.tag}`">{{ FEATURE_TAGS[feature.tag] }}</span>
      <p class="fdl-blurb">{{ feature.blurb }}</p>
      <feature-demo v-if="feature.demo" :demo="feature.demo" class="fdl-demo"/>
      <ul v-if="feature.detail?.length" class="fdl-detail">
        <li v-for="line in feature.detail" :key="line">
          <v-icon size="16" icon="mdi-check-circle" class="fdl-check"/>
          <span>{{ line }}</span>
        </li>
      </ul>
      <NuxtLink v-if="feature.more" :to="feature.more.to" class="fdl-more">
        {{ feature.more.label }}
        <v-icon size="16" icon="mdi-arrow-right"/>
      </NuxtLink>
      <p v-if="feature.tag !== 'soon'" class="fdl-note">Free with Tap Leagues.* <commission-note/></p>
    </template>
    <template #actions>
      <marketing-cta-btn v-if="feature?.tag !== 'soon'" color="primary">{{ accountCtaLabel }}</marketing-cta-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.fdl-note {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin: 14px 0 0;
}

.fdl-tag {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 3px 9px;
  margin-bottom: 8px;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));

  &--soon {
    color: rgb(var(--v-theme-charcoal));
    background: rgb(var(--v-theme-light));
    border: 1px solid rgb(var(--v-theme-border));
  }

  &--app {
    color: rgb(var(--v-theme-info));
    background: #E8F1FB;
  }
}

.fdl-blurb {
  font-size: 15px;
  line-height: 1.6;
  color: rgb(var(--v-theme-charcoal));
}

.fdl-demo {
  margin-top: 16px;
}

.fdl-detail {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
  display: grid;
  gap: 9px;

  li {
    display: flex;
    gap: 9px;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.55;
  }
}

.fdl-check {
  color: rgb(var(--v-theme-success));
  margin-top: 2px;
  flex: none;
}

.fdl-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 16px;
  font-size: 14px;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
</style>
